<template>
	<div class="crRoot">
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div v-if="loading && !data" class="loadingInline"><ScaleLoader /> Loading…</div>

		<template v-if="data">
			<h1 class="pageHeading">{{t.repo}} push {{t.pushId}}</h1>
			<div class="row">
				<span class="badge" :class="statusClass(t.status)">{{label(t.status)}}</span>
				<span v-if="t.statusReason" class="preText">{{t.statusReason}}</span>
				<button type="button" @click="load" :disabled="loading">Refresh</button>
				<span v-if="loading" class="loadingInline"><ScaleLoader /></span>
			</div>

			<section class="card" aria-labelledby="crpPushHeading">
				<h2 id="crpPushHeading" class="secondaryHeading">Push</h2>
				<table class="infoTable">
					<tbody>
						<tr><th scope="row">Pushed</th><td>{{formatTime(t.pushedAt)}} by {{t.pusher || 'unknown'}} <span v-if="t.pusherEmail" class="hintText">({{t.pusherEmail}})</span></td></tr>
						<tr><th scope="row">Ref</th><td class="mono">{{t.refName}}</td></tr>
						<tr><th scope="row">Range</th><td class="mono">{{t.oldSha}}..<a v-if="t.newShaUrl" :href="t.newShaUrl" target="_blank" rel="noopener">{{t.newSha}}</a><template v-else>{{t.newSha}}</template></td></tr>
						<tr><th scope="row">Discovered</th><td>{{formatTime(t.discoveredAt)}}</td></tr>
						<tr><th scope="row">Notified</th><td>{{t.notifiedAt ? formatTime(t.notifiedAt) : 'no'}}</td></tr>
						<tr><th scope="row">Jobs</th><td>review {{t.reviewJobCount}} ({{t.reviewFailures}} failed), triage {{t.triageJobCount}} ({{t.triageFailures}} failed)<template v-if="t.retryAfter">; next attempt after {{formatTime(t.retryAfter)}}</template></td></tr>
					</tbody>
				</table>
				<h3 class="subHeading">Commits ({{t.commits.length}})</h3>
				<ul class="compactList">
					<li v-for="c in t.commits" :key="c.sha"><a v-if="c.url" :href="c.url" target="_blank" rel="noopener" class="mono">{{shortSha(c.sha)}}</a><span v-else class="mono">{{shortSha(c.sha)}}</span> {{c.subject}} <span v-if="c.author" class="hintText">({{c.author}})</span></li>
				</ul>
			</section>

			<section class="card tableCard" aria-labelledby="crpFindingsHeading">
				<h2 id="crpFindingsHeading" class="secondaryHeading">Findings ({{data.findings.length}})</h2>
				<table v-if="data.findings.length" class="dense">
					<thead>
						<tr>
							<th scope="col">ID</th>
							<th scope="col">Severity</th>
							<th scope="col">File</th>
							<th scope="col">Title</th>
							<th scope="col">Status</th>
							<th scope="col">Verdict</th>
							<th scope="col">Introduced</th>
							<th scope="col">Notify</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="f in data.findings" :key="f.id" class="clickable" @click="openFinding(f, $event)">
							<td class="nowrap"><router-link :to="{ name: 'codeReviewFinding', params: { id: f.id } }">{{f.displayId}}</router-link></td>
							<td><span class="badge" :class="severityClass(f.effectiveSeverity)">{{f.effectiveSeverity}}</span></td>
							<td class="fileCell">{{location(f)}}</td>
							<td class="titleCell">{{f.title}}<span v-if="f.duplicateOfId" class="hintText"> (duplicate of CR-{{f.duplicateOfId}})</span></td>
							<td><span class="badge" :class="statusClass(f.status)">{{label(f.status)}}</span></td>
							<td>{{f.verdict}}</td>
							<td>{{f.introducedByPush}}</td>
							<td>{{f.notify ? (f.notifiedAt ? 'sent' : 'yes') : ''}}</td>
						</tr>
					</tbody>
				</table>
				<div v-else class="hintText">No findings stored for this push.</div>
			</section>

			<section class="card" aria-labelledby="crpRunsHeading">
				<h2 id="crpRunsHeading" class="secondaryHeading">Agent runs ({{t.runs.length}})</h2>
				<div v-if="!t.runs.length" class="hintText">No runs yet.</div>
				<div v-for="r in t.runs" :key="r.id" class="run">
					<div class="row">
						<b>#{{r.id}} {{label(r.kind)}}</b>
						<span class="badge" :class="statusClass(r.status)">{{label(r.status)}}</span>
						<span>{{r.model}}<template v-if="r.effort">, effort {{r.effort}}</template></span>
						<span>{{formatCost(r.costUsd)}}</span>
						<span>{{formatDurationMs(r.durationMs)}}<template v-if="r.durationApiMs"> (API {{formatDurationMs(r.durationApiMs)}})</template></span>
						<span v-if="r.numTurns !== null && r.numTurns !== undefined">{{r.numTurns}} turns</span>
						<span v-if="r.permissionDenials">{{r.permissionDenials}} permission denials</span>
						<span v-if="r.subagentCount">{{r.subagentCount}} subagents</span>
					</div>
					<div class="hintText">
						Started {{formatTime(r.startedAt)}}<template v-if="r.endedAt">, ended {{formatTime(r.endedAt)}}</template>
						· agent {{r.agentName || '?'}} job <span class="mono">{{r.agentJobId || '?'}}</span>
						<template v-if="r.promptVersion"> · prompt {{r.promptVersion}}</template>
						<template v-if="r.claudeCodeVersion"> · Claude Code {{r.claudeCodeVersion}}</template>
					</div>
					<ul v-if="r.statusReasons.length" class="compactList reasons">
						<li v-for="(s, i) in r.statusReasons" :key="i">{{s}}</li>
					</ul>
					<table v-if="r.modelUsage.length" class="usageTable">
						<thead>
							<tr><th scope="col">Model</th><th scope="col">Input</th><th scope="col">Output</th><th scope="col">Cache read</th><th scope="col">Cache write</th><th scope="col">Cost</th></tr>
						</thead>
						<tbody>
							<tr v-for="m in r.modelUsage" :key="m.model">
								<td class="mono">{{m.model}}</td>
								<td>{{formatCount(m.inputTokens)}}</td>
								<td>{{formatCount(m.outputTokens)}}</td>
								<td>{{formatCount(m.cacheReadTokens)}}</td>
								<td>{{formatCount(m.cacheWriteTokens)}}</td>
								<td>{{formatCost(m.costUsd)}}</td>
							</tr>
						</tbody>
					</table>
					<details v-if="data.isAdmin" class="rawOutput" @toggle="rawToggled(r, $event)">
						<summary>Raw output (admin)</summary>
						<div v-if="raw[r.id] && raw[r.id].loading" class="loadingInline"><ScaleLoader /> Loading…</div>
						<div v-else-if="raw[r.id] && raw[r.id].error" class="errorBox">{{raw[r.id].error}}</div>
						<template v-else-if="raw[r.id] && raw[r.id].data">
							<div v-if="!raw[r.id].data.found" class="hintText">No output stored for this run.</div>
							<template v-else>
								<div class="subHeading">Result JSON</div>
								<pre class="scrollPre">{{prettyJson(raw[r.id].data.resultJson)}}</pre>
								<div class="subHeading">Log tail</div>
								<pre class="scrollPre">{{raw[r.id].data.logTail || '(empty)'}}</pre>
							</template>
						</template>
					</details>
				</div>
			</section>
		</template>
	</div>
</template>

<script>
	import { GetTarget, GetRunOutput } from 'appRoot/api/CodeReviewData';
	import { label, severityClass, statusClass, formatTime, formatCost, formatCount, formatDurationMs, location, shortSha } from './codeReviewFormat';

	export default {
		props:
		{
			targetId: {
				type: String,
				required: true
			}
		},
		data()
		{
			return {
				data: null,
				loading: false,
				error: null,
				loadToken: 0,
				raw: {}
			};
		},
		created()
		{
			this.load();
		},
		watch:
		{
			targetId()
			{
				this.data = null;
				this.raw = {};
				this.load();
			}
		},
		computed:
		{
			t()
			{
				return this.data ? this.data.target : {};
			}
		},
		methods:
		{
			label,
			severityClass,
			statusClass,
			formatTime,
			formatCost,
			formatCount,
			formatDurationMs,
			location,
			shortSha,
			async load()
			{
				let token = ++this.loadToken;
				this.loading = true;
				this.error = null;
				try
				{
					let data = await GetTarget(this.targetId);
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						this.data = data;
						document.title = data.target.repo + ' push ' + data.target.pushId + ' - ' + appContext.systemName;
					}
					else
						this.error = data.error;
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
						this.loading = false;
				}
			},
			openFinding(f, e)
			{
				if (e && e.target && e.target.closest && e.target.closest('a'))
					return;
				this.$router.push({ name: 'codeReviewFinding', params: { id: f.id } });
			},
			async rawToggled(run, e)
			{
				if (!e.target.open || (this.raw[run.id] && (this.raw[run.id].loading || this.raw[run.id].data)))
					return;
				this.raw = Object.assign({}, this.raw, { [run.id]: { loading: true } });
				let entry;
				try
				{
					let data = await GetRunOutput(run.id);
					entry = data.success ? { data: data } : { error: data.error };
				}
				catch (ex)
				{
					entry = { error: ex.message };
				}
				this.raw = Object.assign({}, this.raw, { [run.id]: entry });
			},
			prettyJson(s)
			{
				if (!s)
					return '(empty)';
				try
				{
					return JSON.stringify(JSON.parse(s), null, 2);
				}
				catch (ex)
				{
					return s;
				}
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.subHeading
	{
		font-size: 14px;
		font-weight: bold;
		margin: 0.8em 0 0.2em 0;
	}

	.compactList
	{
		margin: 4px 0;
		padding-left: 20px;
	}

	.tableCard
	{
		overflow-x: auto;
	}

	.run
	{
		border-top: 1px solid #E2E2E2;
		padding: 6px 0;
	}

	.reasons
	{
		color: #660000;
	}

	.usageTable
	{
		border-collapse: collapse;
		margin: 4px 0;
		font-size: 12px;
	}

		.usageTable th,
		.usageTable td
		{
			border: 1px solid #CCCCCC;
			padding: 2px 8px;
			text-align: left;
		}

		.usageTable th
		{
			background-color: #F0F0F0;
		}

	.rawOutput summary
	{
		cursor: pointer;
		color: #0B57A6;
		margin-top: 4px;
	}
</style>
